import {
    call,
    delay,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash.get';
import { stopSubmit } from 'redux-form';
// @ts-ignore
import { submittingSelectors } from 'shared';

// Constants
import {
    EVENTS_LOAD_REQUESTED,
    EVENTS_POLLING_REQUESTED,
    EVENTS_POLLING_CANCELED,
    EVENT_LOAD_REQUESTED,
    EVENT_CREATE_REQUESTED,
    EVENT_UPDATE_REQUESTED
} from './constants';

// Actions
// @ts-ignore
import setHash from '../hashes';
import { setContainerGroups } from '../containerGroups/actions';
// @ts-ignore
import { mergeDocumentSets } from '../documentSets/actions';
import {
    succeededLoadEvents,
    failedLoadEvents,
    succeededPollingEvents,
    failedPollingEvents,
    succeededLoadEvent,
    failedLoadEvent,
    succeededCreateEvent,
    failedCreateEvent,
    succeededUpdateEvent,
    failedUpdateEvent,
    setEvents,
    mergeEvents,
    setEvent
} from './actions';
import { updateQueryString } from '../router/actions';

// Selectors
// @ts-ignore
import { idTokenSelector } from '../auth/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
// @ts-ignore
import { selectCurrentSeasonId } from '../currentSeasonId/selectors';
// @ts-ignore
import { selectCurrentEntityId } from '../currentEntityId/selectors';
// @ts-ignore
import { makeSelectHash } from '../hashes/selectors';
import { selectIsOnline } from '../connection/selectors';
// @ts-ignore
import { makeSelectLoading } from '../loading/selectors';
// HACK: disobeys folder structure rules
import {
    selectFirstDate,
    selectLastDate
    // @ts-ignore
} from '../../scenes/Harvest/scenes/PickSchedule/components/EventsTimeline/selectors';

// Workspaces
import { makeSelectQueryStringValue } from '../../workspaces/react-router-selectors';

// Utils
// @ts-ignore
import axiosRetry from '../../utils/axiosRetry';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* getEvents(config?: any): any {
    // Select Current Organization Id
    const organizationId = yield select(selectCurrentOrganizationId);

    // Select Current Season Id
    const seasonId = yield select(selectCurrentSeasonId);

    // Select Current Entity Id
    const entityId = yield select(selectCurrentEntityId);

    // Select First Date
    const firstDate = yield select(selectFirstDate);

    // Select Last Date
    const lastDate = yield select(selectLastDate);

    const url = `${REACT_APP_API_URL}/v1/Events`;

    return yield call(axiosRetry, {
        method: 'GET',
        url,
        ...(config || {}),
        params: {
            ...(config && config.params ? config.params : {}),
            organizationId,
            filter: {
                require: {
                    $and: [
                        {
                            deletedAt: { $exists: false },
                            'entity.archivedAt': { $exists: false },
                            ...(seasonId
                                ? { 'entity.seasonId': seasonId }
                                : {}),
                            'schema.eventTypeId': { $exists: true },
                            ...(entityId
                                ? {
                                      $or: [
                                          {
                                              'entity.path': {
                                                  $like: `/${entityId}/%`
                                              }
                                          },
                                          {
                                              entityId
                                          }
                                      ]
                                  }
                                : {})
                        },
                        {
                            $or: [
                                {
                                    startDate: {
                                        $lte: lastDate
                                    },
                                    endDate: {
                                        $gte: firstDate
                                    }
                                },
                                {
                                    startDate: {
                                        $lte: lastDate
                                    },
                                    endDate: {
                                        $gte: firstDate
                                    }
                                }
                            ]
                        }
                    ]
                }
            }
        }
    });
}

export function* loadEvents(): any {
    // HACK need to be  global
    const namespace = 'events';

    try {
        const response = yield call(getEvents);

        const { data } = response;

        const hash = get(response, 'headers.x-hash');

        // Succeeded Load Events
        yield put(succeededLoadEvents(response));

        // Set Events
        yield put(setEvents(data));

        // Set Hash
        yield put(setHash(namespace, hash));
    } catch (error) {
        yield put(failedLoadEvents(error));
    }
}

export function* watchEventsLoadRequested(): any {
    yield takeLatest(EVENTS_LOAD_REQUESTED, loadEvents);
}

export function* pollEvents(action?: any): any {
    // HACK need to be  global
    const namespace = 'events';

    while (true) {
        yield delay(120000);

        // Select Is Online
        const isOnline = yield select(selectIsOnline);

        // Select Loading
        const isLoading = yield select(makeSelectLoading(), {
            namespace: ['EVENTS_LOAD', 'EVENT_LOAD']
        });

        // Select Is Submitting
        const isSubmitting = yield select(
            submittingSelectors.selectIsSubmitting()
        );

        const seasonalEventModalActive = yield select(
            makeSelectQueryStringValue('seasonalEventModal')
        );

        // Select Hash
        const hash = yield select(makeSelectHash(namespace));
        let hashMatch = false;

        if (
            isOnline &&
            !isLoading &&
            !isSubmitting &&
            !seasonalEventModalActive
        ) {
            try {
                if (hash) {
                    const hashResponse = yield call(getEvents, {
                        params: { hash: true }
                    });

                    hashMatch = hash === get(hashResponse, 'headers.x-hash');
                }
                if (!hashMatch) {
                    const response = yield call(getEvents);

                    const { data } = response;
                    const nextHash = get(response, 'headers.x-hash');

                    // Succeeded Polling Events
                    yield put(succeededPollingEvents(response));

                    // Set Events
                    yield put(setEvents(data));

                    // Set Hash
                    yield put(setHash(namespace, nextHash));
                }
            } catch (error) {
                // Failed Polling Events
                yield put(failedPollingEvents(error));
            }
        }
    }
}

export function* watchEventsPollingRequested(): any {
    while (true) {
        const action = yield take(EVENTS_POLLING_REQUESTED);
        yield race([call(pollEvents, action), take(EVENTS_POLLING_CANCELED)]);
    }
}

export function* getEvent(action: any): any {
    const {
        payload: { eventId }
    } = action;

    const idToken = yield select(idTokenSelector);
    const organizationId = yield select(selectCurrentOrganizationId);

    const url = `${REACT_APP_API_URL}/v1/Data`;

    const config = {
        headers: {
            Authorization: idToken
        },
        params: {
            organizationId,
            shape: {
                events: {
                    model: 'Events',
                    filter: {
                        require: {
                            deletedAt: { $exists: false },
                            'entity.archivedAt': { $exists: false },
                            id: eventId
                        }
                    }
                },
                containerGroups: {
                    model: 'ContainerGroups',
                    filter: {
                        require: {
                            deletedAt: { $exists: false },
                            'event.entity.archivedAt': { $exists: false },
                            'event.id': eventId
                        }
                    }
                },
                harvestEvents: {
                    model: 'HarvestEvents',
                    filter: {
                        require: {
                            deletedAt: { $exists: false },
                            'event.entity.archivedAt': { $exists: false },
                            'event.id': eventId
                        },
                        fields: [
                            'id',
                            'harvestBinSuitabilityId',
                            'harvestPalletTypeId',
                            'harvestStorageTypeId',
                            'harvestSubmissionProfileId',
                            'harvestGrowingMethodId',
                            'harvestLocationId',
                            'harvestExporterId',
                            'harvestBrandId',
                            'pickTypeId'
                        ]
                    }
                },
                documentSets: {
                    model: 'DocumentSets',
                    filter: {
                        require: {
                            deletedAt: { $exists: false },
                            'event.entity.archivedAt': { $exists: false },
                            'event.id': eventId
                        },
                        eager: '[assignedUsers, documentSetAttributes]'
                    }
                }
            }
        }
    };

    try {
        const response = yield call(axios.get, url, config);

        // Merge Event
        yield put(mergeEvents(response.data.events));

        // Set Container Groups
        yield put(setContainerGroups(response.data.containerGroups));

        // Merge Document Sets
        yield put(mergeDocumentSets(response.data.documentSets));

        // Succeeded Load Event
        yield put(succeededLoadEvent(response));
    } catch (error) {
        yield put(failedLoadEvent(error));
    } finally {
        // Provide window in which the task can't be called for the same event
        yield delay(5000);
    }
}

export function* watchEventLoadRequested(): any {
    yield takeLatest(EVENT_LOAD_REQUESTED, getEvent);
}

export function* postEvent(action: any): any {
    const {
        payload: { data }
    } = action;

    const idToken = yield select(idTokenSelector);

    const url = `${REACT_APP_API_URL}/v1/Events`;

    const config = {
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response = yield call(
            axios.post,
            url,
            {
                ...data,
                groupId: data.groupId || undefined
            },
            config
        );
        const { jobs, ...event } = response.data;

        // Succeeded Create Event
        yield put(succeededCreateEvent(response));

        // Set Event
        yield put(setEvent(event));

        // Merge Document Sets
        yield put(mergeDocumentSets(jobs));

        // Update Query String
        yield put(
            updateQueryString({
                seasonalEventModal: undefined,
                selectedEvent: undefined,
                generalEventModal: undefined,
                generalEventDuplicateModal: undefined,
                selectedSchema: undefined,
                newEvent: event.id,
                selectedCurrentEntityId: undefined,
                newGroup: undefined,
                seasonalEventDefaultValues: undefined
            })
        );
    } catch (error) {
        yield put(failedCreateEvent(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('eventForm', { _error: message }));
        yield put(stopSubmit('generalEventForm', { _error: message }));
    }
}

export function* watchEventCreateRequested(): any {
    yield takeLatest(EVENT_CREATE_REQUESTED, postEvent);
}

export function* patchEvent(action: any): any {
    const {
        payload: { eventId, data }
    } = action;

    const idToken = yield select(idTokenSelector);

    const url = `${REACT_APP_API_URL}/v1/Events/${eventId}`;

    const config = {
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response = yield call(axios.patch, url, data, config);
        const { jobs, quantitySchedules, ...event } = response.data;

        // Succeeded Update Event
        yield put(succeededUpdateEvent(response));

        // Set Event
        yield put(setEvent(event));

        // Merge Document Sets
        yield put(mergeDocumentSets(jobs));

        // Update Query String
        yield put(
            updateQueryString({
                seasonalEventModal: data.quantitySchedules
                    ? 'updated'
                    : undefined,
                selectedEvent: data.quantitySchedules ? event.id : undefined,
                generalEventModal: undefined,
                generalEventDuplicateModal: undefined,
                selectedCurrentEntityId: undefined,
                newGroup: undefined,
                seasonalEventDefaultValues: undefined,
                eventPrintModal: undefined
            })
        );
    } catch (error) {
        yield put(failedUpdateEvent(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('eventForm', { _error: message }));
        yield put(stopSubmit('generalEventForm', { _error: message }));
    }
}

export function* watchEventUpdateRequested(): any {
    yield takeLatest(EVENT_UPDATE_REQUESTED, patchEvent);
}
